import React, { useEffect, useMemo, useState } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Field from "../ui/Field";
import Switch from "../ui/Switch";
import {
    ArrowUp,
    ArrowLeft,
    ArrowRight,
    ArrowDown,
    RotateCcw,
    RotateCw, RotateCcwSquare, RotateCwSquare,
} from "lucide-react";
import HallSchemeEditorCanvas, {
    HallScheme,
    SchemeSector,
    SchemeSeat,
    SchemeStandingPlace,
    SchemeTextLabel,
    SchemeShape,
    ScaleValue,
    ToolKey,
} from "../hall-schemes/HallSchemeEditorCanvas";
import {
    bulkUpsertHallSchemeElementsRequest,
    bulkUpsertHallSchemeSectionsRequest,
    getHallSchemeElementsRequest,
    getHallSchemeRequest,
    getHallSchemeSectionsRequest,
} from "../../services/api/hallSchemes.api";

type HallSchemeEditorModalProps = {
    isOpen: boolean;
    hallName?: string;
    scheme: HallScheme | null;
    onClose: () => void;
    onSave?: (payload: HallScheme) => void;
};

const createSectorId = () => `section-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const isTempClientId = (id?: string | null) => {
    if (!id) {
        return true;
    }

    return (
        id.startsWith("seat-") ||
        id.startsWith("row-") ||
        id.startsWith("standing-") ||
        id.startsWith("text-") ||
        id.startsWith("shape-") ||
        id.startsWith("section-")
    );
};

const normalizePersistedId = (id?: string | null) => {
    if (!id || isTempClientId(id)) {
        return undefined;
    }

    return id;
};
const omitNullableValues = <T extends Record<string, unknown>>(value: T): T => {
    return Object.fromEntries(
        Object.entries(value).filter(([, entryValue]) => entryValue != null)
    ) as T;
};

const HallSchemeEditorModal: React.FC<HallSchemeEditorModalProps> = ({
                                                                         isOpen,
                                                                         hallName,
                                                                         scheme,
                                                                         onClose,
                                                                         onSave,
                                                                     }) => {
    const [schemeName, setSchemeName] = useState(scheme?.name ?? "");
    const [selectedTool, setSelectedTool] = useState<ToolKey>("select");
    const [scale, setScale] = useState<ScaleValue>(1);

    const [sectors, setSectors] = useState<SchemeSector[]>([]);
    const [seats, setSeats] = useState<SchemeSeat[]>([]);
    const [selectedSectorId, setSelectedSectorId] = useState<string | null>(null);
    const [selectedSeatIds, setSelectedSeatIds] = useState<string[]>([]);

    const [newSectorName, setNewSectorName] = useState("");
    const [newSectorColor, setNewSectorColor] = useState("#6d7cff");

    const [standingPlaces, setStandingPlaces] = useState<SchemeStandingPlace[]>([]);
    const [selectedStandingPlaceId, setSelectedStandingPlaceId] = useState<string | null>(null);

    const [textLabels, setTextLabels] = useState<SchemeTextLabel[]>([]);
    const [selectedTextLabelId, setSelectedTextLabelId] = useState<string | null>(null);

    const [shapes, setShapes] = useState<SchemeShape[]>([]);
    const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);

    const [isLoadingScheme, setIsLoadingScheme] = useState(false);
    const [isSavingScheme, setIsSavingScheme] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
    const [renumberStartValue, setRenumberStartValue] = useState("1");
    // console.log(scheme)

    const title = useMemo(() => {
        if (hallName) {
            return `Редактор схемы — ${hallName}`;
        }

        return "Редактор схемы";
    }, [hallName]);

    const selectedSector = useMemo(() => {
        return sectors.find((item) => item.id === selectedSectorId) ?? null;
    }, [sectors, selectedSectorId]);



    const selectedStandingPlace = useMemo(() => {
        if (!selectedStandingPlaceId) {
            return null;
        }

        return standingPlaces.find((item) => item.id === selectedStandingPlaceId) ?? null;
    }, [selectedStandingPlaceId, standingPlaces]);

    const selectedTextLabel = useMemo(() => {
        if (!selectedTextLabelId) {
            return null;
        }

        return textLabels.find((item) => item.id === selectedTextLabelId) ?? null;
    }, [selectedTextLabelId, textLabels]);

    const selectedShape = useMemo(() => {
        if (!selectedShapeId) {
            return null;
        }

        return shapes.find((item) => item.id === selectedShapeId) ?? null;
    }, [selectedShapeId, shapes]);

    const selectedSeat = useMemo(() => {
        if (selectedSeatIds.length !== 1) {
            return null;
        }

        return seats.find((seat) => seat.id === selectedSeatIds[0]) ?? null;
    }, [selectedSeatIds, seats]);

    const selectedSeats = useMemo(() => {
        return seats.filter((seat) => selectedSeatIds.includes(seat.id));
    }, [seats, selectedSeatIds]);

    const sharedSelectedRowNumber = useMemo(() => {
        if (selectedSeats.length === 0) {
            return null;
        }

        const firstRowNumber = selectedSeats[0]?.rowNumber ?? "";

        return selectedSeats.every((seat) => seat.rowNumber === firstRowNumber)
            ? firstRowNumber
            : null;
    }, [selectedSeats]);

    useEffect(() => {
        setSchemeName(scheme?.name ?? "");
    }, [scheme?.id, scheme?.name]);

    useEffect(() => {
        if (!isOpen || !scheme?.id) {
            return;
        }

        let isCancelled = false;

        setIsLoadingScheme(true);
        setLoadError(null);
        setSaveError(null);
        setSaveSuccess(null);

        setSectors([]);
        setSeats([]);
        setStandingPlaces([]);
        setTextLabels([]);
        setShapes([]);
        setSelectedSectorId(null);
        setSelectedSeatIds([]);
        setSelectedStandingPlaceId(null);
        setSelectedTextLabelId(null);
        setSelectedShapeId(null);
        setSelectedTool("select");


        const loadSchemeData = async () => {
            setIsLoadingScheme(true);
            setLoadError(null);
            setSaveError(null);
            setSaveSuccess(null);


            try {
                const [schemeResponse, sectionsResponse, elementsResponse] = await Promise.all([
                    getHallSchemeRequest(scheme.id),
                    getHallSchemeSectionsRequest(scheme.id),
                    getHallSchemeElementsRequest(scheme.id),
                ]);

                if (isCancelled) {
                    return;
                }

                setSchemeName(schemeResponse.name ?? "");

                const nextSectors: SchemeSector[] = sectionsResponse
                    .filter((item) => item.is_active !== false)
                    .map((item) => ({
                        id: item.id,
                        name: item.name,
                        color: item.color || "#6d7cff",
                    }));

                const nextSeats: SchemeSeat[] = [];
                const nextStandingPlaces: SchemeStandingPlace[] = [];
                const nextTextLabels: SchemeTextLabel[] = [];
                const nextShapes: SchemeShape[] = [];

                elementsResponse
                    .filter((item) => item.is_active !== false)
                    .forEach((item) => {
                        if (item.type === "seat") {
                            nextSeats.push({
                                id: item.id ?? `seat-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                                seatNumber: String(item.seat_number ?? 0),
                                rowNumber: String(item.row_number ?? 0),
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                rowType: item.row_type === "vertical" ? "vertical" : "horizontal",
                                sectorId: item.sector_id ?? null,
                                rowGroupId:
                                    item.row_group_id ??
                                    `row-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                            });
                        }

                        if (item.type === "standing-place") {
                            nextStandingPlaces.push({
                                id: item.id ?? `standing-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                width: item.width ?? 0,
                                height: item.height ?? 0,
                                text: item.text ?? "",
                                sectorId: item.sector_id ?? null,
                            });
                        }

                        if (item.type === "text") {
                            nextTextLabels.push({
                                id: item.id ?? `text-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                width: item.width ?? 120,
                                height: item.height ?? 32,
                                text: item.text ?? "",
                            });
                        }

                        if (item.type === "shape") {
                            nextShapes.push({
                                id: item.id ?? `shape-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                                x: item.x ?? 0,
                                y: item.y ?? 0,
                                width: item.width ?? 120,
                                height: item.height ?? 80,
                                shapeType: item.shape_type ?? "rectangle",
                                borderRadius: Number(item.border_radius ?? 12),
                                isOval: Boolean(item.isOval),
                            });
                        }
                    });

                setSectors(nextSectors);
                setSeats(nextSeats);
                setStandingPlaces(nextStandingPlaces);
                setTextLabels(nextTextLabels);
                setShapes(nextShapes);

                setSelectedSectorId(null);
                setSelectedSeatIds([]);
                setSelectedStandingPlaceId(null);
                setSelectedTextLabelId(null);
                setSelectedShapeId(null);
                setSelectedTool("select");
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                setLoadError(error instanceof Error ? error.message : "Не удалось загрузить схему.");
            } finally {
                if (!isCancelled) {
                    setIsLoadingScheme(false);
                }
            }
        };

        void loadSchemeData();

        return () => {
            isCancelled = true;
        };
    }, [isOpen, scheme?.id]);

    const updateSelectedStandingPlace = (patch: Partial<SchemeStandingPlace>) => {
        if (!selectedStandingPlaceId) {
            return;
        }

        setStandingPlaces((prev) =>
            prev.map((item) => (item.id === selectedStandingPlaceId ? { ...item, ...patch } : item))
        );
    };

    const updateSelectedTextLabel = (patch: Partial<SchemeTextLabel>) => {
        if (!selectedTextLabelId) {
            return;
        }

        setTextLabels((prev) =>
            prev.map((item) => (item.id === selectedTextLabelId ? { ...item, ...patch } : item))
        );
    };

    const updateSelectedShape = (patch: Partial<SchemeShape>) => {
        if (!selectedShapeId) return;

        setShapes((prev) =>
            prev.map((item) =>
                item.id === selectedShapeId
                    ? {
                        ...item,
                        ...patch,
                        isOval:
                            patch.isOval !== undefined
                                ? Boolean(patch.isOval)
                                : item.isOval,
                        borderRadius:
                            patch.borderRadius !== undefined
                                ? Math.max(0, Number(patch.borderRadius) || 0)
                                : item.borderRadius,
                    }
                    : item
            )
        );
    };
    const updateSelectedSeatsRowNumber = (rowNumber: string) => {
        if (selectedSeatIds.length === 0) {
            return;
        }

        setSeats((prev) =>
            prev.map((seat) =>
                selectedSeatIds.includes(seat.id)
                    ? {
                        ...seat,
                        rowNumber,
                    }
                    : seat
            )
        );
    };
    const updateSingleSelectedSeat = (patch: Partial<SchemeSeat>) => {
        if (!selectedSeat) {
            return;
        }

        setSeats((prev) =>
            prev.map((seat) => (seat.id === selectedSeat.id ? { ...seat, ...patch } : seat))
        );
    };

    const updateSelectedSector = (patch: Partial<SchemeSector>) => {
        if (!selectedSectorId) {
            return;
        }

        setSectors((prev) =>
            prev.map((item) => (item.id === selectedSectorId ? { ...item, ...patch } : item))
        );
    };

    const handleAddSector = () => {
        const name = newSectorName.trim();

        if (!name) {
            return;
        }

        const nextSector: SchemeSector = {
            id: createSectorId(),
            name,
            color: newSectorColor,
        };

        setSectors((prev) => [...prev, nextSector]);
        setSelectedSectorId(nextSector.id);
        setSelectedTool("sector");
        setNewSectorName("");
    };

    const assignSelectedSeatsToSector = () => {
        if (!selectedSectorId || selectedSeatIds.length === 0) {
            return;
        }

        setSeats((prev) =>
            prev.map((seat) =>
                selectedSeatIds.includes(seat.id)
                    ? {
                        ...seat,
                        sectorId: selectedSectorId,
                    }
                    : seat
            )
        );
    };

    const moveSelectedSeats = (dx: number, dy: number) => {
        if (selectedSeatIds.length === 0) {
            return;
        }

        setSeats((prev) =>
            prev.map((seat) =>
                selectedSeatIds.includes(seat.id)
                    ? {
                        ...seat,
                        x: seat.x + dx,
                        y: seat.y + dy,
                    }
                    : seat
            )
        );
    };

    const rotateSelectedSeats = (angleDelta: number) => {
        if (selectedSeatIds.length === 0) {
            return;
        }

        const selectedSeats = seats.filter((seat) => selectedSeatIds.includes(seat.id));

        if (selectedSeats.length === 0) {
            return;
        }

        const centerX = selectedSeats.reduce((sum, seat) => sum + seat.x, 0) / selectedSeats.length;
        const centerY = selectedSeats.reduce((sum, seat) => sum + seat.y, 0) / selectedSeats.length;
        const radians = (angleDelta * Math.PI) / 180;

        setSeats((prev) =>
            prev.map((seat) => {
                if (!selectedSeatIds.includes(seat.id)) {
                    return seat;
                }

                const relativeX = seat.x - centerX;
                const relativeY = seat.y - centerY;

                const rotatedX = relativeX * Math.cos(radians) - relativeY * Math.sin(radians);
                const rotatedY = relativeX * Math.sin(radians) + relativeY * Math.cos(radians);

                return {
                    ...seat,
                    x: Math.round(rotatedX + centerX),
                    y: Math.round(rotatedY + centerY),
                };
            })
        );
    };

    // const handleSave = async () => {
    //     if (!scheme?.id) {
    //         return;
    //     }
    //
    //     setIsSavingScheme(true);
    //     setSaveError(null);
    //     setSaveSuccess(null);
    //
    //     try {
    //         const sectionsPayload = sectors.map((sector) => {
    //             const payload: Record<string, unknown> = {
    //                 scheme_id: scheme.id,
    //                 name: sector.name,
    //                 color: sector.color,
    //                 is_active: true,
    //             };
    //
    //             const persistedId = normalizePersistedId(sector.id);
    //
    //             if (persistedId) {
    //                 payload.id = persistedId;
    //             }
    //
    //             return omitNullableValues(payload);
    //         });
    //
    //         await bulkUpsertHallSchemeSectionsRequest(scheme.id, sectionsPayload);
    //
    //         const seatElements = seats.map((seat) => {
    //             const payload: Record<string, unknown> = {
    //                 scheme_id: scheme.id,
    //                 sector_id: seat.sectorId,
    //                 type: "seat",
    //                 x: seat.x,
    //                 y: seat.y,
    //                 width: 18,
    //                 height: 18,
    //                 rotation: 0,
    //                 z_index: 1,
    //                 is_locked: false,
    //                 is_hidden: false,
    //                 is_active: true,
    //                 text: null,
    //                 seat_number: String(seat.seatNumber),
    //                 row_number: String(seat.rowNumber),
    //                 row_type: seat.rowType,
    //                 row_group_id: seat.rowGroupId,
    //                 shape_type: null,
    //                 border_radius: null,
    //                 isOval: null,
    //                 font_size: null,
    //                 font_weight: null,
    //                 text_align: null,
    //                 white_space: null,
    //             };
    //
    //             const persistedId = normalizePersistedId(seat.id);
    //
    //             if (persistedId) {
    //                 payload.id = persistedId;
    //             }
    //
    //             return omitNullableValues(payload);
    //         });
    //
    //         const standingPlaceElements = standingPlaces.map((place) => {
    //             const payload: Record<string, unknown> = {
    //                 scheme_id: scheme.id,
    //                 sector_id: place.sectorId,
    //                 type: "standing-place",
    //                 x: place.x,
    //                 y: place.y,
    //                 width: place.width,
    //                 height: place.height,
    //                 rotation: 0,
    //                 z_index: 1,
    //                 is_locked: false,
    //                 is_hidden: false,
    //                 is_active: true,
    //                 text: place.text,
    //                 seat_number: null,
    //                 row_number: null,
    //                 row_type: null,
    //                 row_group_id: null,
    //                 shape_type: null,
    //                 border_radius: null,
    //                 isOval: null,
    //                 font_size: null,
    //                 font_weight: null,
    //                 text_align: null,
    //                 white_space: null,
    //             };
    //
    //             const persistedId = normalizePersistedId(place.id);
    //
    //             if (persistedId) {
    //                 payload.id = persistedId;
    //             }
    //
    //             return omitNullableValues(payload);
    //         });
    //
    //         const textElements = textLabels.map((textLabel) => {
    //             const payload: Record<string, unknown> = {
    //                 scheme_id: scheme.id,
    //                 sector_id: null,
    //                 type: "text",
    //                 x: textLabel.x,
    //                 y: textLabel.y,
    //                 width: textLabel.width,
    //                 height: textLabel.height,
    //                 rotation: 0,
    //                 z_index: 1,
    //                 is_locked: false,
    //                 is_hidden: false,
    //                 is_active: true,
    //                 text: textLabel.text,
    //                 seat_number: null,
    //                 row_number: null,
    //                 row_type: null,
    //                 row_group_id: null,
    //                 shape_type: null,
    //                 border_radius: null,
    //                 isOval: null,
    //                 font_size: 14,
    //                 font_weight: 600,
    //                 text_align: "center",
    //                 white_space: "nowrap",
    //             };
    //
    //             const persistedId = normalizePersistedId(textLabel.id);
    //
    //             if (persistedId) {
    //                 payload.id = persistedId;
    //             }
    //
    //             return omitNullableValues(payload);
    //         });
    //
    //         const shapeElements = shapes.map((shape) => {
    //             const payload: Record<string, unknown> = {
    //                 scheme_id: scheme.id,
    //                 sector_id: null,
    //                 type: "shape",
    //                 x: shape.x,
    //                 y: shape.y,
    //                 width: shape.width,
    //                 height: shape.height,
    //                 rotation: 0,
    //                 z_index: 0,
    //                 is_locked: false,
    //                 is_hidden: false,
    //                 is_active: true,
    //                 text: null,
    //                 seat_number: null,
    //                 row_number: null,
    //                 row_type: null,
    //                 row_group_id: null,
    //                 shape_type: shape.shapeType,
    //                 border_radius: shape.borderRadius,
    //                 isOval: shape.isOval,
    //                 font_size: null,
    //                 font_weight: null,
    //                 text_align: null,
    //                 white_space: null,
    //             };
    //
    //             const persistedId = normalizePersistedId(shape.id);
    //
    //             if (persistedId) {
    //                 payload.id = persistedId;
    //             }
    //
    //             return omitNullableValues(payload);
    //         });
    //
    //         await bulkUpsertHallSchemeElementsRequest(scheme.id, [
    //             ...seatElements,
    //             ...standingPlaceElements,
    //             ...textElements,
    //             ...shapeElements,
    //         ]);
    //
    //         const payload: HallScheme = {
    //             id: scheme.id,
    //             hall_id: scheme.hall_id,
    //             name: schemeName.trim() || scheme.name,
    //         };
    //
    //         onSave?.(payload);
    //         setSaveSuccess("Схема успешно сохранена.");
    //     } catch (error) {
    //         setSaveError(error instanceof Error ? error.message : "Не удалось сохранить схему.");
    //     } finally {
    //         setIsSavingScheme(false);
    //     }
    // };

    const handleSave = async () => {
        if (!scheme?.id) {
            return;
        }

        type SavedSection = {
            id: string;
            name: string;
            color?: string | null;
        };

        type SavedElement = {
            id: string;
            sector_id?: string | null;
            type: string;
            x?: number | null;
            y?: number | null;
            width?: number | null;
            height?: number | null;
            text?: string | null;
            seat_number?: string | null;
            row_number?: string | null;
            row_type?: "horizontal" | "vertical" | null;
            row_group_id?: string | null;
            shape_type?: string | null;
            border_radius?: number | null;
            isOval?: boolean | null;
        };

        setIsSavingScheme(true);
        setSaveError(null);
        setSaveSuccess(null);

        try {
            const sectionsPayload = sectors.map((sector) => {
                const payload: Record<string, unknown> = {
                    scheme_id: scheme.id,
                    name: sector.name,
                    color: sector.color,
                    is_active: true,
                };

                const persistedId = normalizePersistedId(sector.id);

                if (persistedId) {
                    payload.id = persistedId;
                }

                return omitNullableValues(payload);
            });

            const savedSectionsResponse = await bulkUpsertHallSchemeSectionsRequest(
                scheme.id,
                sectionsPayload,
            );

            const savedSections = (savedSectionsResponse ?? []) as SavedSection[];

            const sectorIdMap = new Map<string, string>();
            const remainingSections = [...savedSections];

            const updatedSectors = sectors.map((sector) => {
                const matchIndex = remainingSections.findIndex(
                    (savedSection) =>
                        savedSection.name === sector.name &&
                        (savedSection.color ?? null) === (sector.color ?? null),
                );

                if (matchIndex === -1) {
                    return sector;
                }

                const matched = remainingSections[matchIndex];
                remainingSections.splice(matchIndex, 1);

                sectorIdMap.set(sector.id, matched.id);

                return {
                    ...sector,
                    id: matched.id,
                    name: matched.name ?? sector.name,
                    color: matched.color ?? sector.color,
                };
            });

            setSectors(updatedSectors);

            const remapSectorId = (sectorId: string | null) => {
                if (!sectorId) {
                    return null;
                }

                return sectorIdMap.get(sectorId) ?? sectorId;
            };

            const seatElements = seats.map((seat) => {
                const payload: Record<string, unknown> = {
                    scheme_id: scheme.id,
                    sector_id: remapSectorId(seat.sectorId),
                    type: "seat",
                    x: seat.x,
                    y: seat.y,
                    width: 18,
                    height: 18,
                    rotation: 0,
                    z_index: 1,
                    is_locked: false,
                    is_hidden: false,
                    is_active: true,
                    text: null,
                    seat_number: String(seat.seatNumber),
                    row_number: String(seat.rowNumber),
                    row_type: seat.rowType,
                    row_group_id: seat.rowGroupId,
                    shape_type: null,
                    border_radius: null,
                    isOval: null,
                    font_size: null,
                    font_weight: null,
                    text_align: null,
                    white_space: null,
                };

                const persistedId = normalizePersistedId(seat.id);

                if (persistedId) {
                    payload.id = persistedId;
                }

                return omitNullableValues(payload);
            });

            const standingPlaceElements = standingPlaces.map((place) => {
                const payload: Record<string, unknown> = {
                    scheme_id: scheme.id,
                    sector_id: remapSectorId(place.sectorId),
                    type: "standing-place",
                    x: place.x,
                    y: place.y,
                    width: place.width,
                    height: place.height,
                    rotation: 0,
                    z_index: 1,
                    is_locked: false,
                    is_hidden: false,
                    is_active: true,
                    text: place.text,
                    seat_number: null,
                    row_number: null,
                    row_type: null,
                    row_group_id: null,
                    shape_type: null,
                    border_radius: null,
                    isOval: null,
                    font_size: null,
                    font_weight: null,
                    text_align: null,
                    white_space: null,
                };

                const persistedId = normalizePersistedId(place.id);

                if (persistedId) {
                    payload.id = persistedId;
                }

                return omitNullableValues(payload);
            });

            const textElements = textLabels.map((textLabel) => {
                const payload: Record<string, unknown> = {
                    scheme_id: scheme.id,
                    sector_id: null,
                    type: "text",
                    x: textLabel.x,
                    y: textLabel.y,
                    width: textLabel.width,
                    height: textLabel.height,
                    rotation: 0,
                    z_index: 1,
                    is_locked: false,
                    is_hidden: false,
                    is_active: true,
                    text: textLabel.text,
                    seat_number: null,
                    row_number: null,
                    row_type: null,
                    row_group_id: null,
                    shape_type: null,
                    border_radius: null,
                    isOval: null,
                    font_size: 14,
                    font_weight: 600,
                    text_align: "center",
                    white_space: "nowrap",
                };

                const persistedId = normalizePersistedId(textLabel.id);

                if (persistedId) {
                    payload.id = persistedId;
                }

                return omitNullableValues(payload);
            });

            const shapeElements = shapes.map((shape) => {
                const payload: Record<string, unknown> = {
                    scheme_id: scheme.id,
                    sector_id: null,
                    type: "shape",
                    x: shape.x,
                    y: shape.y,
                    width: shape.width,
                    height: shape.height,
                    rotation: 0,
                    z_index: 0,
                    is_locked: false,
                    is_hidden: false,
                    is_active: true,
                    text: null,
                    seat_number: null,
                    row_number: null,
                    row_type: null,
                    row_group_id: null,
                    shape_type: shape.shapeType,
                    border_radius: shape.borderRadius,
                    isOval: shape.isOval,
                    font_size: null,
                    font_weight: null,
                    text_align: null,
                    white_space: null,
                };

                const persistedId = normalizePersistedId(shape.id);

                if (persistedId) {
                    payload.id = persistedId;
                }

                return omitNullableValues(payload);
            });

            const savedElementsResponse = await bulkUpsertHallSchemeElementsRequest(
                scheme.id,
                [
                    ...seatElements,
                    ...standingPlaceElements,
                    ...textElements,
                    ...shapeElements,
                ],
            );

            const savedElements = (savedElementsResponse ?? []) as SavedElement[];
            const remainingElements = [...savedElements];

            const updatedSeats = seats.map((seat) => {
                const matchIndex = remainingElements.findIndex(
                    (element) =>
                        element.type === "seat" &&
                        Number(element.x ?? 0) === seat.x &&
                        Number(element.y ?? 0) === seat.y &&
                        String(element.seat_number ?? "") === String(seat.seatNumber) &&
                        String(element.row_number ?? "") === String(seat.rowNumber) &&
                        (element.row_type ?? null) === seat.rowType &&
                        (element.row_group_id ?? null) === seat.rowGroupId &&
                        (element.sector_id ?? null) === remapSectorId(seat.sectorId),
                );

                if (matchIndex === -1) {
                    return {
                        ...seat,
                        sectorId: remapSectorId(seat.sectorId),
                    };
                }

                const matched = remainingElements[matchIndex];
                remainingElements.splice(matchIndex, 1);

                return {
                    ...seat,
                    id: matched.id,
                    sectorId: matched.sector_id ?? remapSectorId(seat.sectorId),
                };
            });

            const seatIdMap = new Map<string, string>();
            seats.forEach((seat, index) => {
                seatIdMap.set(seat.id, updatedSeats[index].id);
            });

            setSeats(updatedSeats);
            setSelectedSeatIds((prev) =>
                prev.map((id) => seatIdMap.get(id) ?? id),
            );

            const updatedStandingPlaces = standingPlaces.map((place) => {
                const matchIndex = remainingElements.findIndex(
                    (element) =>
                        element.type === "standing-place" &&
                        Number(element.x ?? 0) === place.x &&
                        Number(element.y ?? 0) === place.y &&
                        Number(element.width ?? 0) === place.width &&
                        Number(element.height ?? 0) === place.height &&
                        (element.text ?? "") === place.text &&
                        (element.sector_id ?? null) === remapSectorId(place.sectorId),
                );

                if (matchIndex === -1) {
                    return {
                        ...place,
                        sectorId: remapSectorId(place.sectorId),
                    };
                }

                const matched = remainingElements[matchIndex];
                remainingElements.splice(matchIndex, 1);

                return {
                    ...place,
                    id: matched.id,
                    sectorId: matched.sector_id ?? remapSectorId(place.sectorId),
                };
            });

            const standingPlaceIdMap = new Map<string, string>();
            standingPlaces.forEach((place, index) => {
                standingPlaceIdMap.set(place.id, updatedStandingPlaces[index].id);
            });

            setStandingPlaces(updatedStandingPlaces);
            setSelectedStandingPlaceId((prev) =>
                prev ? standingPlaceIdMap.get(prev) ?? prev : null,
            );

            const updatedTextLabels = textLabels.map((textLabel) => {
                const matchIndex = remainingElements.findIndex(
                    (element) =>
                        element.type === "text" &&
                        Number(element.x ?? 0) === textLabel.x &&
                        Number(element.y ?? 0) === textLabel.y &&
                        Number(element.width ?? 0) === textLabel.width &&
                        Number(element.height ?? 0) === textLabel.height &&
                        (element.text ?? "") === textLabel.text,
                );

                if (matchIndex === -1) {
                    return textLabel;
                }

                const matched = remainingElements[matchIndex];
                remainingElements.splice(matchIndex, 1);

                return {
                    ...textLabel,
                    id: matched.id,
                };
            });

            const textLabelIdMap = new Map<string, string>();
            textLabels.forEach((textLabel, index) => {
                textLabelIdMap.set(textLabel.id, updatedTextLabels[index].id);
            });

            setTextLabels(updatedTextLabels);
            setSelectedTextLabelId((prev) =>
                prev ? textLabelIdMap.get(prev) ?? prev : null,
            );

            const updatedShapes = shapes.map((shape) => {
                const matchIndex = remainingElements.findIndex(
                    (element) =>
                        element.type === "shape" &&
                        Number(element.x ?? 0) === shape.x &&
                        Number(element.y ?? 0) === shape.y &&
                        Number(element.width ?? 0) === shape.width &&
                        Number(element.height ?? 0) === shape.height &&
                        (element.shape_type ?? null) === shape.shapeType &&
                        Number(element.border_radius ?? 0) === Number(shape.borderRadius) &&
                        Boolean(element.isOval ?? false) === Boolean(shape.isOval),
                );

                if (matchIndex === -1) {
                    return shape;
                }

                const matched = remainingElements[matchIndex];
                remainingElements.splice(matchIndex, 1);

                return {
                    ...shape,
                    id: matched.id,
                };
            });

            const shapeIdMap = new Map<string, string>();
            shapes.forEach((shape, index) => {
                shapeIdMap.set(shape.id, updatedShapes[index].id);
            });

            setShapes(updatedShapes);
            setSelectedShapeId((prev) =>
                prev ? shapeIdMap.get(prev) ?? prev : null,
            );

            setSelectedSectorId((prev) =>
                prev ? sectorIdMap.get(prev) ?? prev : null,
            );

            const payload: HallScheme = {
                id: scheme.id,
                hall_id: scheme.hall_id,
                name: schemeName.trim() || scheme.name,
            };

            onSave?.(payload);
            setSaveSuccess("Схема успешно сохранена.");
        } catch (error) {
            setSaveError(
                error instanceof Error ? error.message : "Не удалось сохранить схему.",
            );
        } finally {
            setIsSavingScheme(false);
        }
    };

    const renumberSeatsPreservingDirection = (inputSeats: SchemeSeat[]): SchemeSeat[] => {
        const grouped = new Map<string, SchemeSeat[]>();

        inputSeats.forEach((seat) => {
            if (!grouped.has(seat.rowGroupId)) {
                grouped.set(seat.rowGroupId, []);
            }

            grouped.get(seat.rowGroupId)?.push(seat);
        });

        const normalizedSeats: SchemeSeat[] = [];

        grouped.forEach((groupSeats) => {
            if (groupSeats.length === 0) {
                return;
            }

            const axisSorted = [...groupSeats].sort((a, b) => {
                if (a.rowType === "horizontal") {
                    return a.x - b.x;
                }

                return a.y - b.y;
            });

            const firstSeat = axisSorted[0];
            const lastSeat = axisSorted[axisSorted.length - 1];

            const firstNumber = Number(firstSeat.seatNumber);
            const lastNumber = Number(lastSeat.seatNumber);

            const isForwardDirection = firstNumber <= lastNumber;

            const orderedSeats = isForwardDirection ? axisSorted : [...axisSorted].reverse();

            orderedSeats.forEach((seat, index) => {
                normalizedSeats.push({
                    ...seat,
                    seatNumber: String(index + 1),
                });
            });
        });

        return normalizedSeats;
    };
    const getSeatGroupKey = (seat: SchemeSeat) => {
        const normalizedRow = String(seat.rowNumber ?? "").trim();
        const normalizedSector = seat.sectorId ? String(seat.sectorId).trim() : "";

        if (normalizedSector) {
            return `${normalizedRow}::${normalizedSector}`;
        }

        return `${normalizedRow}::__no_sector__`;
    };

    const renumberSelectedSeats = (direction: "left" | "right") => {
        if (selectedSeatIds.length === 0 || !sharedSelectedRowNumber) {
            return;
        }

        const startNumber = Number(renumberStartValue);

        if (!Number.isFinite(startNumber)) {
            return;
        }

        const selectedSeatsMap = new Map(
            seats
                .filter((seat) => selectedSeatIds.includes(seat.id))
                .map((seat) => [seat.id, seat]),
        );

        const selectedSeatsList = Array.from(selectedSeatsMap.values());

        if (selectedSeatsList.length === 0) {
            return;
        }

        const firstSeat = selectedSeatsList[0];
        const targetGroupKey = getSeatGroupKey(firstSeat);

        const sameGroupSelectedSeats = selectedSeatsList.filter(
            (seat) => getSeatGroupKey(seat) === targetGroupKey,
        );

        const sortedSeats = [...sameGroupSelectedSeats].sort((a, b) => {
            if (a.rowType === "horizontal") {
                return direction === "right" ? a.x - b.x : b.x - a.x;
            }

            return direction === "right" ? a.y - b.y : b.y - a.y;
        });

        const nextSeatNumberMap = new Map<string, string>();

        sortedSeats.forEach((seat, index) => {
            nextSeatNumberMap.set(seat.id, String(startNumber + index));
        });

        setSeats((prev) =>
            prev.map((seat) =>
                nextSeatNumberMap.has(seat.id)
                    ? {
                        ...seat,
                        seatNumber: nextSeatNumberMap.get(seat.id) ?? seat.seatNumber,
                    }
                    : seat,
            ),
        );
    };

    const renumberSeatsAfterDelete = (
        allSeats: SchemeSeat[],
        removedSeatIds: string[],
    ): SchemeSeat[] => {
        const removedSeats = allSeats.filter((seat) => removedSeatIds.includes(seat.id));
        const affectedGroupKeys = new Set(removedSeats.map(getSeatGroupKey));

        const nextSeats = allSeats.filter((seat) => !removedSeatIds.includes(seat.id));

        const groupedSeats = new Map<string, SchemeSeat[]>();

        nextSeats.forEach((seat) => {
            const groupKey = getSeatGroupKey(seat);

            if (!groupedSeats.has(groupKey)) {
                groupedSeats.set(groupKey, []);
            }

            groupedSeats.get(groupKey)?.push(seat);
        });

        return nextSeats.map((seat) => {
            const groupKey = getSeatGroupKey(seat);

            if (!affectedGroupKeys.has(groupKey)) {
                return seat;
            }

            const groupSeats = groupedSeats.get(groupKey) ?? [];

            const axisSorted = [...groupSeats].sort((a, b) => {
                if (a.rowType === "horizontal") {
                    return a.x - b.x;
                }

                return a.y - b.y;
            });

            const firstSeat = axisSorted[0];
            const lastSeat = axisSorted[axisSorted.length - 1];

            const firstNumber = Number(firstSeat?.seatNumber ?? 0);
            const lastNumber = Number(lastSeat?.seatNumber ?? 0);
            const isForwardDirection = firstNumber <= lastNumber;

            const orderedSeats = isForwardDirection ? axisSorted : [...axisSorted].reverse();
            const newIndex = orderedSeats.findIndex((item) => item.id === seat.id);

            if (newIndex === -1) {
                return seat;
            }

            return {
                ...seat,
                seatNumber: String(newIndex + 1),
            };
        });
    };
    if (!isOpen) {
        return null;
    }

    return (
        <div className="scheme-editor-modal" role="dialog" aria-modal="true" aria-label="Редактор схемы зала">
            <div className="scheme-editor-modal-content">
                <header className="scheme-editor-modal-header">
                    <div className="scheme-editor-header-main">
                        <p className="scheme-editor-header-eyebrow">Hall Scheme Editor</p>
                        <h2 className="scheme-editor-header-title">{title}</h2>
                    </div>

                    <div className="scheme-editor-header-actions">
                        <Button type="button" variant="secondary" onClick={onClose}>
                            Закрыть
                        </Button>

                        <Button type="button" onClick={() => void handleSave()} disabled={!scheme || isSavingScheme}>
                            {isSavingScheme ? "Сохранение..." : "Сохранить"}
                        </Button>
                    </div>
                </header>

                <div className="scheme-editor-modal-body">
                    <aside className="scheme-editor-tools">
                        <section className="scheme-editor-section">
                            <h3 className="scheme-editor-section-title">Информация о схеме</h3>

                            <div className="scheme-editor-info-list">
                                <div className="scheme-editor-info-item">
                                    <span className="scheme-editor-info-label">Название</span>
                                    <span className="scheme-editor-info-value">
                                        {schemeName || scheme?.name || "Без названия"}
                                    </span>
                                </div>
                                <div className="scheme-editor-info-item">
                                    <span className="scheme-editor-info-label">ID схемы</span>
                                    <p className="scheme-editor-tool-properties-hint">
                                        {scheme?.id}
                                    </p>
                                </div>
                            </div>

                            {isLoadingScheme ? (
                                <p className="scheme-editor-tool-properties-hint">Загрузка схемы...</p>
                            ) : null}

                            {loadError ? (
                                <p className="scheme-editor-tool-properties-hint" style={{ color: "#ef4444" }}>
                                    {loadError}
                                </p>
                            ) : null}

                            {saveError ? (
                                <p className="scheme-editor-tool-properties-hint" style={{ color: "#ef4444" }}>
                                    {saveError}
                                </p>
                            ) : null}

                            {saveSuccess ? (
                                <p className="scheme-editor-tool-properties-hint" style={{ color: "#16a34a" }}>
                                    {saveSuccess}
                                </p>
                            ) : null}
                        </section>

                        {selectedTool === "sector" ? (
                            <>
                                <section className="scheme-editor-section">
                                    <h3 className="scheme-editor-section-title">Добавить секцию</h3>

                                    <Field label="Название секции" htmlFor="new-section-name">
                                        <Input
                                            id="new-section-name"
                                            value={newSectorName}
                                            onChange={(event) => setNewSectorName(event.target.value)}
                                            placeholder="Например, Партер A"
                                        />
                                    </Field>

                                    <div className="scheme-editor-color-row">
                                        <span className="scheme-editor-info-label">Цвет</span>
                                        <input
                                            type="color"
                                            className="scheme-editor-color-input"
                                            value={newSectorColor}
                                            onChange={(event) => setNewSectorColor(event.target.value)}
                                        />
                                    </div>

                                    <div className="editor-actions">
                                        <Button type="button" onClick={handleAddSector}>
                                            Добавить секцию
                                        </Button>
                                    </div>
                                </section>

                                <section className="scheme-editor-section">
                                    <h3 className="scheme-editor-section-title">Секции</h3>

                                    {sectors.length > 0 ? (
                                        <div className="scheme-editor-section-list">
                                            {sectors.map((sector) => (
                                                <button
                                                    key={sector.id}
                                                    type="button"
                                                    className={`scheme-editor-section-item ${selectedSectorId === sector.id ? "scheme-editor-section-item-active" : ""}`}
                                                    onClick={() => setSelectedSectorId(sector.id)}
                                                >
                                                    <span
                                                        className="scheme-editor-section-swatch"
                                                        style={{ backgroundColor: sector.color }}
                                                    />
                                                    <span className="scheme-editor-section-item-text">{sector.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="empty-block">
                                            <h3 className="empty-block-title">Секций пока нет</h3>
                                            <p className="empty-block-text">
                                                Добавьте первую секцию, чтобы потом привязывать к ней места.
                                            </p>
                                        </div>
                                    )}

                                    {selectedSector ? (
                                        <section className="scheme-editor-section">
                                            <h3 className="scheme-editor-section-title">Свойства секции</h3>

                                            <Field label="Название секции" htmlFor="selected-sector-name">
                                                <Input
                                                    id="selected-sector-name"
                                                    value={selectedSector.name}
                                                    onChange={(event) =>
                                                        updateSelectedSector({ name: event.target.value })
                                                    }
                                                />
                                            </Field>

                                            <div className="scheme-editor-color-row">
                                                <span className="scheme-editor-info-label">Цвет</span>
                                                <input
                                                    type="color"
                                                    className="scheme-editor-color-input"
                                                    value={selectedSector.color}
                                                    onChange={(event) =>
                                                        updateSelectedSector({ color: event.target.value })
                                                    }
                                                />
                                            </div>

                                            <div className="editor-actions">
                                                <Button
                                                    type="button"
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setSectors((prev) =>
                                                            prev.filter((item) => item.id !== selectedSector.id)
                                                        );
                                                        setSelectedSectorId(null);
                                                        setSeats((prev) =>
                                                            prev.map((seat) =>
                                                                seat.sectorId === selectedSector.id
                                                                    ? { ...seat, sectorId: null }
                                                                    : seat
                                                            )
                                                        );
                                                        setStandingPlaces((prev) =>
                                                            prev.map((item) =>
                                                                item.sectorId === selectedSector.id
                                                                    ? { ...item, sectorId: null }
                                                                    : item
                                                            )
                                                        );
                                                    }}
                                                >
                                                    Удалить секцию
                                                </Button>
                                            </div>
                                        </section>
                                    ) : null}
                                </section>
                            </>
                        ) : null}

                        {selectedTool === "select" && selectedSeatIds.length > 0 ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Выделенные места</h3>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Выбрано мест: <strong>{selectedSeatIds.length}</strong>
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        Выберите секцию из уже созданных и назначьте её выделенным местам.
                                    </p>
                                </div>

                                <Field label="Секция для выделенных мест" htmlFor="assign-section-select">
                                    <select
                                        id="assign-section-select"
                                        className="ui-select"
                                        value={selectedSectorId ?? ""}
                                        onChange={(event) => setSelectedSectorId(event.target.value || null)}
                                    >
                                        <option value="">Выберите секцию</option>
                                        {sectors.map((sector) => (
                                            <option key={sector.id} value={sector.id}>
                                                {sector.name}
                                            </option>
                                        ))}
                                    </select>
                                </Field>

                                <div className="editor-actions">
                                    <Button
                                        type="button"
                                        onClick={assignSelectedSeatsToSector}
                                        disabled={!selectedSectorId || selectedSeatIds.length === 0}
                                    >
                                        Назначить секцию выделенным местам
                                    </Button>
                                </div>

                                <section className="scheme-editor-section">
                                    <h3 className="scheme-editor-section-title">Трансформация выделенных мест</h3>

                                    <div className="scheme-editor-transform-grid">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => moveSelectedSeats(0, -10)}
                                            aria-label="Сдвинуть вверх"
                                            title="Сдвинуть вверх"
                                        >
                                            <ArrowUp size={16} />
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => moveSelectedSeats(-10, 0)}
                                            aria-label="Сдвинуть влево"
                                            title="Сдвинуть влево"
                                        >
                                            <ArrowLeft size={16} />
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => moveSelectedSeats(10, 0)}
                                            aria-label="Сдвинуть вправо"
                                            title="Сдвинуть вправо"
                                        >
                                            <ArrowRight size={16} />
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => moveSelectedSeats(0, 10)}
                                            aria-label="Сдвинуть вниз"
                                            title="Сдвинуть вниз"
                                        >
                                            <ArrowDown size={16} />
                                        </Button>
                                    </div>

                                    <div className="scheme-editor-transform-grid">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => rotateSelectedSeats(-5)}
                                            aria-label="Повернуть влево"
                                            title="Повернуть влево"
                                        >
                                            <RotateCcw size={16} />
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => rotateSelectedSeats(5)}
                                            aria-label="Повернуть вправо"
                                            title="Повернуть вправо"
                                        >
                                            <RotateCw size={16} />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => rotateSelectedSeats(-90)}
                                            aria-label="Повернуть влево"
                                            title="Повернуть влево"

                                        >
                                            <RotateCcwSquare size={16}  />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={() => rotateSelectedSeats(90)}
                                            aria-label="Повернуть вправо"
                                            title="Повернуть вправо"
                                        >
                                            <RotateCwSquare size={16} />
                                        </Button>
                                    </div>
                                </section>

                                {selectedSeat ? (
                                    <section className="scheme-editor-section">
                                        <h3 className="scheme-editor-section-title">Свойства выбранного места</h3>

                                        <Field label="Номер ряда" htmlFor="selected-seat-row-number">
                                            <Input
                                                id="selected-seat-row-number"
                                                type="text"
                                                value={selectedSeat.rowNumber ?? ""}
                                                onChange={(event) =>
                                                    updateSingleSelectedSeat({
                                                        rowNumber: event.target.value,
                                                    } as Partial<SchemeSeat>)
                                                }
                                            />
                                        </Field>

                                        <Field label="Номер места" htmlFor="selected-seat-number">
                                            <Input
                                                id="selected-seat-number"
                                                type="text"
                                                value={selectedSeat.seatNumber ?? ""}
                                                onChange={(event) =>
                                                    updateSingleSelectedSeat({
                                                        seatNumber: event.target.value,
                                                    } as Partial<SchemeSeat>)
                                                }
                                            />
                                        </Field>

                                        <Field label="Тип ряда" htmlFor="selected-seat-row-type">
                                            <select
                                                id="selected-seat-row-type"
                                                className="ui-select"
                                                value={selectedSeat.rowType}
                                                onChange={(event) =>
                                                    updateSingleSelectedSeat({
                                                        rowType: event.target.value as "horizontal" | "vertical",
                                                    })
                                                }
                                            >
                                                <option value="horizontal">Горизонтальный ряд</option>
                                                <option value="vertical">Вертикальный ряд</option>
                                            </select>
                                        </Field>
                                    </section>
                                ) : null}
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        const normalizedSeats = renumberSeatsAfterDelete(seats, selectedSeatIds);
                                        setSeats(normalizedSeats);
                                        setSelectedSeatIds([]);
                                    }}
                                    disabled={selectedSeatIds.length === 0}
                                >
                                    Удалить выделенные места
                                </Button>
                                {/*<Button*/}
                                {/*    type="button"*/}
                                {/*    variant="secondary"*/}
                                {/*    onClick={() => {*/}
                                {/*        const nextSeats = seats.filter((seat) => !selectedSeatIds.includes(seat.id));*/}
                                {/*        const grouped = new Map<string, SchemeSeat[]>();*/}

                                {/*        nextSeats.forEach((seat) => {*/}
                                {/*            if (!grouped.has(seat.rowGroupId)) {*/}
                                {/*                grouped.set(seat.rowGroupId, []);*/}
                                {/*            }*/}

                                {/*            grouped.get(seat.rowGroupId)?.push(seat);*/}
                                {/*        });*/}

                                {/*        const normalizedSeats: SchemeSeat[] = [];*/}

                                {/*        grouped.forEach((groupSeats) => {*/}
                                {/*            const sorted = [...groupSeats].sort((a, b) => {*/}
                                {/*                if (a.rowType === "horizontal") {*/}
                                {/*                    return a.x - b.x;*/}
                                {/*                }*/}

                                {/*                return a.y - b.y;*/}
                                {/*            });*/}

                                {/*            sorted.forEach((seat, index) => {*/}
                                {/*                normalizedSeats.push({*/}
                                {/*                    ...seat,*/}
                                {/*                    seatNumber: String(index + 1),*/}
                                {/*                });*/}
                                {/*            });*/}
                                {/*        });*/}

                                {/*        setSeats(normalizedSeats);*/}
                                {/*        setSelectedSeatIds([]);*/}
                                {/*    }}*/}
                                {/*    disabled={selectedSeatIds.length === 0}*/}
                                {/*>*/}
                                {/*    Удалить выделенные места*/}
                                {/*</Button>*/}
                            </section>
                        ) : null}
                        {selectedSeatIds.length > 1 && sharedSelectedRowNumber !== null ? (
                            <Field label="Номер ряда" htmlFor="selected-seats-row-number">
                                <Input
                                    id="selected-seats-row-number"
                                    type="text"
                                    value={sharedSelectedRowNumber}
                                    onChange={(event) => updateSelectedSeatsRowNumber(event.target.value)}
                                />
                            </Field>
                        ) : null}
                        {selectedSeatIds.length > 1 && sharedSelectedRowNumber !== null ? (
                            <section className="scheme-editor-section">

                                <h3 className="scheme-editor-section-title">Нумерация ряда</h3>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Выбрано <strong>{selectedSeatIds.length}</strong> мест одного ряда
                                    </p>
                                    <p className="scheme-editor-tool-properties-hint">
                                        Укажите стартовый номер и выберите направление нумерации.
                                    </p>
                                </div>

                                <Field label="Начать с номера" htmlFor="selected-seats-renumber-start">
                                    <Input
                                        id="selected-seats-renumber-start"
                                        type="number"
                                        min={1}
                                        value={renumberStartValue}
                                        onChange={(event) => setRenumberStartValue(event.target.value)}
                                    />
                                </Field>

                                <div className="editor-actions">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => renumberSelectedSeats("left")}
                                    >
                                        Пронумеровать места влево
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => renumberSelectedSeats("right")}
                                    >
                                        Пронумеровать места вправо
                                    </Button>
                                </div>
                            </section>
                        ) : null}

                        {selectedStandingPlace ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Свойства стоячего места</h3>

                                <Field label="Текст" htmlFor="standing-place-text">
                                    <Input
                                        id="standing-place-text"
                                        value={selectedStandingPlace.text}
                                        onChange={(event) =>
                                            updateSelectedStandingPlace({ text: event.target.value })
                                        }
                                    />
                                </Field>

                                {/* Ширина/высота блока стоячих мест — редактируются вручную, в т.ч. после
                                    автоматического импорта из партнёрского API (там размер берётся из
                                    партнёрских данных и может требовать ручной подгонки под нашу схему). */}
                                <div className="scheme-editor-tool-properties-row">
                                    <Field label="Ширина" htmlFor="standing-place-width">
                                        <Input
                                            id="standing-place-width"
                                            type="number"
                                            min={1}
                                            value={selectedStandingPlace.width}
                                            onChange={(event) => {
                                                const nextWidth = Number(event.target.value);
                                                if (!Number.isFinite(nextWidth) || nextWidth <= 0) {
                                                    return;
                                                }
                                                updateSelectedStandingPlace({ width: nextWidth });
                                            }}
                                        />
                                    </Field>

                                    <Field label="Высота" htmlFor="standing-place-height">
                                        <Input
                                            id="standing-place-height"
                                            type="number"
                                            min={1}
                                            value={selectedStandingPlace.height}
                                            onChange={(event) => {
                                                const nextHeight = Number(event.target.value);
                                                if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
                                                    return;
                                                }
                                                updateSelectedStandingPlace({ height: nextHeight });
                                            }}
                                        />
                                    </Field>
                                </div>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Позиция: <strong>{Math.round(selectedStandingPlace.x)}</strong> /{" "}
                                        <strong>{Math.round(selectedStandingPlace.y)}</strong>
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        Стоячее место можно перетаскивать по сетке. Ширину и высоту блока можно
                                        менять полями выше — например, после автоматического импорта из партнёрской
                                        схемы зала.
                                    </p>
                                </div>

                                <div className="editor-actions">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => {
                                            setStandingPlaces((prev) =>
                                                prev.filter((item) => item.id !== selectedStandingPlace.id)
                                            );
                                            setSelectedStandingPlaceId(null);
                                        }}
                                    >
                                        Удалить стоячее место
                                    </Button>
                                </div>
                            </section>
                        ) : null}

                        {selectedTextLabel ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Свойства текста</h3>

                                <Field label="Текст" htmlFor="text-label-text">
                                    <Input
                                        id="text-label-text"
                                        value={selectedTextLabel.text}
                                        onChange={(event) => updateSelectedTextLabel({ text: event.target.value })}
                                    />
                                </Field>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Позиция: <strong>{Math.round(selectedTextLabel.x)}</strong> /{" "}
                                        <strong>{Math.round(selectedTextLabel.y)}</strong>
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        Текст можно выделить, перетаскивать мышкой и удалять как отдельный объект.
                                    </p>
                                </div>

                                <div className="editor-actions">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => {
                                            setTextLabels((prev) =>
                                                prev.filter((item) => item.id !== selectedTextLabel.id)
                                            );
                                            setSelectedTextLabelId(null);
                                        }}
                                    >
                                        Удалить текст
                                    </Button>
                                </div>
                            </section>
                        ) : null}

                        {selectedShape ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Свойства фигуры</h3>

                                <Field label="Радиус скругления" htmlFor="shape-border-radius">
                                    <Input
                                        id="shape-border-radius"
                                        type="number"
                                        min="0"
                                        value={String(selectedShape.borderRadius)}
                                        onChange={(event) =>
                                            updateSelectedShape({
                                                borderRadius: Math.max(0, Number(event.target.value || 0)),
                                            })
                                        }
                                        disabled={selectedShape.isOval}
                                    />
                                </Field>

                                <Switch
                                    checked={selectedShape.isOval}
                                    onChange={(checked) => updateSelectedShape({ isOval: checked })}
                                    label="Преобразовать в овал"
                                />

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Позиция: <strong>{Math.round(selectedShape.x)}</strong> /{" "}
                                        <strong>{Math.round(selectedShape.y)}</strong>
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        Фигуру можно перемещать мышкой, удалять и менять скругление углов.
                                    </p>
                                </div>

                                <div className="editor-actions">
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={() => {
                                            setShapes((prev) =>
                                                prev.filter((item) => item.id !== selectedShape.id)
                                            );
                                            setSelectedShapeId(null);
                                        }}
                                    >
                                        Удалить фигуру
                                    </Button>
                                </div>
                            </section>
                        ) : null}

                        {(selectedTool === "horizontal-row" || selectedTool === "vertical-row") ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Свойства инструмента</h3>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Активный инструмент:{" "}
                                        <strong>
                                            {selectedTool === "horizontal-row"
                                                ? "горизонтальные ряды"
                                                : "вертикальные ряды"}
                                        </strong>
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        Зажмите мышь в поле и тяните. Места автоматически расставятся по сетке и
                                        пронумеруются по направлению движения.
                                    </p>
                                </div>
                            </section>
                        ) : null}

                        {selectedTool === "text" ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Свойства инструмента</h3>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Активный инструмент: <strong>текст</strong>
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        Потяните мышью по сетке, чтобы создать текстовый блок. Затем его можно выбрать,
                                        перетаскивать, менять текст и удалять.
                                    </p>
                                </div>
                            </section>
                        ) : null}

                        {selectedTool === "shape" ? (
                            <section className="scheme-editor-section">
                                <h3 className="scheme-editor-section-title">Свойства инструмента</h3>

                                <div className="scheme-editor-tool-properties">
                                    <p className="scheme-editor-tool-properties-text">
                                        Активный инструмент: <strong>фигура</strong>
                                    </p>

                                    <p className="scheme-editor-tool-properties-hint">
                                        Потяните мышью по сетке, чтобы создать прямоугольник. После создания можно
                                        менять радиус скругления, включать овал, перемещать и удалять фигуру.
                                    </p>
                                </div>
                            </section>
                        ) : null}

                        {/*<div className="scheme-editor-sticky-actions">*/}
                        {/*    <div className="editor-actions">*/}
                        {/*        <Button type="button" variant="secondary" onClick={onClose}>*/}
                        {/*            Отмена*/}
                        {/*        </Button>*/}

                        {/*        <Button*/}
                        {/*            type="button"*/}
                        {/*            onClick={() => void handleSave()}*/}
                        {/*            disabled={!scheme || isSavingScheme || isLoadingScheme}*/}
                        {/*        >*/}
                        {/*            {isSavingScheme ? "Сохранение..." : "Сохранить схему"}*/}
                        {/*        </Button>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                    </aside>

                    <section className="scheme-editor-canvas-wrap">

                        <div className="scheme-editor-canvas-toolbar">
                            <div className="scheme-editor-toolbar-tools">
                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${selectedTool === "select" ? "scheme-editor-toolbar-tool-active" : ""}`}
                                    onClick={() => setSelectedTool("select")}
                                >
                                    Выбор
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${selectedTool === "sector" ? "scheme-editor-toolbar-tool-active" : ""}`}
                                    onClick={() => setSelectedTool("sector")}
                                >
                                    Секции
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${selectedTool === "horizontal-row" ? "scheme-editor-toolbar-tool-active" : ""}`}
                                    onClick={() => setSelectedTool("horizontal-row")}
                                >
                                    Горизонтальные ряды
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${selectedTool === "vertical-row" ? "scheme-editor-toolbar-tool-active" : ""}`}
                                    onClick={() => setSelectedTool("vertical-row")}
                                >
                                    Вертикальные ряды
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${selectedTool === "standing-place" ? "scheme-editor-toolbar-tool-active" : ""}`}
                                    onClick={() => setSelectedTool("standing-place")}
                                >
                                    Стоячее место
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${selectedTool === "text" ? "scheme-editor-toolbar-tool-active" : ""}`}
                                    onClick={() => setSelectedTool("text")}
                                >
                                    Текст
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-toolbar-tool ${selectedTool === "shape" ? "scheme-editor-toolbar-tool-active" : ""}`}
                                    onClick={() => setSelectedTool("shape")}
                                >
                                    Фигура
                                </button>
                            </div>

                            <div className="scheme-editor-toolbar-spacer" />

                            <div className="scheme-editor-scale-switch">
                                <button
                                    type="button"
                                    className={`scheme-editor-scale-button ${scale === 1 ? "scheme-editor-scale-button-active" : ""}`}
                                    onClick={() => setScale(1)}
                                >
                                    1x
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-scale-button ${scale === 0.8 ? "scheme-editor-scale-button-active" : ""}`}
                                    onClick={() => setScale(0.8)}
                                >
                                    0.8x
                                </button>

                                <button
                                    type="button"
                                    className={`scheme-editor-scale-button ${scale === 0.6 ? "scheme-editor-scale-button-active" : ""}`}
                                    onClick={() => setScale(0.6)}
                                >
                                    0.6x
                                </button>
                            </div>
                        </div>
                        {isLoadingScheme ? (
                            <div className="scheme-editor-canvas-loading">
                                <div className="scheme-editor-canvas-loading-content">
                                    <h3 className="scheme-editor-canvas-loading-title">Загрузка схемы</h3>
                                    <p className="scheme-editor-canvas-loading-text">
                                        Подождите, данные схемы загружаются...
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                        <HallSchemeEditorCanvas
                            tool={selectedTool}
                            scale={scale}
                            sectors={sectors}
                            seats={seats}
                            selectedSeatIds={selectedSeatIds}
                            selectedSectorId={selectedSectorId}
                            onSectorSelect={setSelectedSectorId}
                            onSeatsChange={setSeats}
                            onSelectedSeatsChange={setSelectedSeatIds}
                            standingPlaces={standingPlaces}
                            selectedStandingPlaceId={selectedStandingPlaceId}
                            onStandingPlacesChange={setStandingPlaces}
                            onStandingPlaceSelect={setSelectedStandingPlaceId}
                            textLabels={textLabels}
                            selectedTextLabelId={selectedTextLabelId}
                            onTextLabelsChange={setTextLabels}
                            onTextLabelSelect={setSelectedTextLabelId}
                            shapes={shapes}
                            selectedShapeId={selectedShapeId}
                            onShapesChange={setShapes}
                            onShapeSelect={setSelectedShapeId}
                        />
                        </>)}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default HallSchemeEditorModal;
